/**
 * Theme preference, resolution and application.
 *
 * Three states, not two. The slice used to hold `'light' | 'dark'` and default
 * to `'light'`, which quietly overrode the operating system for everybody who
 * never opens a settings menu — which is most people. `'system'` is the default
 * now, and it is a real value that persists: choosing it is different from
 * never having chosen, because it survives the OS flipping at sunset.
 */
export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** The order the header control cycles through. */
export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

const STORAGE_KEY = 'venue.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function isPreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/** The `matchMedia` list, or null wherever it does not exist (jsdom, SSR). */
export function darkMediaQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(DARK_QUERY);
}

export function prefersDark(): boolean {
  return darkMediaQuery()?.matches ?? false;
}

/**
 * The stored preference, defaulting to `'system'`.
 *
 * A value written before `'system'` existed is still a valid `'light'` or
 * `'dark'`, so an operator who did choose keeps their choice.
 */
export function readStoredTheme(): ThemePreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isPreference(raw) ? raw : 'system';
  } catch {
    // Private mode, or a browser with storage disabled. Not worth failing boot.
    return 'system';
  }
}

export function storeTheme(preference: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    /* see readStoredTheme */
  }
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? (prefersDark() ? 'dark' : 'light') : preference;
}

/**
 * Put the resolved theme on `<html>`.
 *
 * `.dark` is what the CSS switches on; `data-theme` is the same fact in a form
 * a test or a screenshot run can assert on without reading a class list.
 */
export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference);
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    root.dataset.theme = resolved;
    root.dataset.themePreference = preference;
  }
  return resolved;
}
